import React, {
  createContext,
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  ACCESS_TOKEN,
  GUEST_EMAIL,
  GUEST_MOBILE_NUMBER,
  GUEST_USER_NAME,
  REFRESH_TOKEN,
  USER_ID,
} from "../constants";
import {
  saveGuestUser,
  storeUser,
  signOut,
} from "../features/auth/AuthActions";
import { clearUserData } from "../features/user/userActions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../rootReducer";
import { UserDetails } from "../features/auth/AuthModels";
import { getUserGivenDetails } from "../features/user/userActions";
import { clearSelectedLocation } from "../features/restaurant/restaurantAction";
import { clearPlaceOrder } from "../features/cart/cartActions";
import { startEmptyCartOrder } from "../features/order/orderActions";
import { getCookie } from "../../helper/cookieHelper";
interface AuthServiceProps {
  children: ReactNode;
  fallback?: ReactElement;
}

const userStates = ["guest", "guestSignedIn", "signedIn"] as const;

type userStateType = typeof userStates[number];

interface AuthContextType {
  userState: userStateType;
  userAuth: UserDetails | null;
  signOut: any;
  refreshUserData: any;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export default function AuthService({ children, fallback }: AuthServiceProps) {
  const dispatch = useDispatch();

  const isUserSignedIn = useSelector((state: any) => state.auth.signedIn);
  const User = useSelector((state: any) => state.auth.user);
  const GuestUser = useSelector((state: any) => state.auth.guestUser);
  const userID = getCookie(USER_ID)

  const refreshUserData = () => {
    userID && isUserSignedIn && dispatch(getUserGivenDetails());
  };

  const userState = useMemo((): userStateType => {
    if (User?.id && User.accessToken && User.refreshToken) {
      return "signedIn";
    } else if (User?.id && GuestUser) {
      return "guestSignedIn";
    } else {
      return "guest";
    }
  }, [User, GuestUser]);

  // Handle side effects on login transition only (not on initial mount — the mount useEffect at line 84 handles that)
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current && userState === "signedIn") {
      dispatch(clearSelectedLocation());
      refreshUserData();
    }
    hasInitializedRef.current = true;
  }, [userState]);

  const LogOut = useCallback(() => {
    // Clear Local Storage
    // localStorage.clear();
    signOut();
    clearUserData();
    clearPlaceOrder();
    startEmptyCartOrder();
    // Clear Redux Store
  }, []);

  useEffect(() => {
    const userId = getCookie(USER_ID);
    // console.log("userIdCookie 3", userId);

    const accessToken = getCookie(ACCESS_TOKEN);
    // console.log("accessTokenCookie 5", accessToken);
    const accessTokenLocal = localStorage.getItem(ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);

    const guestName = localStorage.getItem(GUEST_USER_NAME);
    const guestMobile = localStorage.getItem(GUEST_MOBILE_NUMBER);
    const guestEmail = localStorage.getItem(GUEST_EMAIL);

    if (userId && accessToken && refreshToken) {
      // Login Flow
      dispatch(
        storeUser({
          id: userId,
          accessToken: accessToken,
          refreshToken: refreshToken,
        })
      );
      refreshUserData();
    } else if (guestName && guestMobile && userId) {
      // Guest Flow
      dispatch(
        storeUser({
          id: userId,
        })
      );

      guestName &&
        guestMobile &&
        guestEmail &&
        dispatch(
          saveGuestUser({
            fullName: guestName,
            mobile: guestMobile,
            email: guestEmail,
            adultSize: 0,
            kidzSize: 0,
          })
        );
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        userState: userState,
        userAuth: User,
        signOut: LogOut,
        refreshUserData: refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
